import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  TypedFormControl,
  validFormControlChanges$,
} from '@principle-theorem/ng-shared';
import { debounceUserInput, isChanged$ } from '@principle-theorem/shared';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export const NATIONAL_HEALTH_INDEX_NUMBER_LENGTH = 7;

@Component({
  selector: 'pr-national-health-index-number-form',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './national-health-index-number-form.component.html',
  styleUrl: './national-health-index-number-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NationalHealthIndexNumberFormComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();

  nationalHealthIndexNumberCtrl = new TypedFormControl<string | undefined>(
    undefined,
    [Validators.minLength(NATIONAL_HEALTH_INDEX_NUMBER_LENGTH)]
  );

  @Input()
  set nationalHealthIndexNumber(nationalHealthIndexNumber: string | undefined) {
    this.nationalHealthIndexNumberCtrl.setValue(nationalHealthIndexNumber, {
      emitEvent: false,
    });
  }

  /**
   * Locking and unlocking must not emit: consumers persist the emitted number.
   */
  @Input({ transform: coerceBooleanProperty })
  set disabled(disabled: boolean) {
    if (disabled === this.nationalHealthIndexNumberCtrl.disabled) {
      return;
    }
    if (disabled) {
      this.nationalHealthIndexNumberCtrl.disable({ emitEvent: false });
      return;
    }
    this.nationalHealthIndexNumberCtrl.enable({ emitEvent: false });
  }

  @Output() nationalHealthIndexNumberChange = new EventEmitter<string>();

  /**
   * Emits every edit immediately, including incomplete numbers and removal,
   * unlike the debounced, valid-only nationalHealthIndexNumberChange.
   */
  @Output() nationalHealthIndexNumberInput = new EventEmitter<
    string | undefined
  >();

  constructor() {
    validFormControlChanges$(this.nationalHealthIndexNumberCtrl)
      .pipe(isChanged$(), debounceUserInput(), takeUntil(this._onDestroy$))
      .subscribe((number) =>
        this.nationalHealthIndexNumberChange.emit(number || undefined)
      );

    this.nationalHealthIndexNumberCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((number) =>
        this.nationalHealthIndexNumberInput.emit(number || undefined)
      );
  }

  removeNationalHealthIndexNumber(): void {
    this.nationalHealthIndexNumberChange.emit(undefined);
    this.nationalHealthIndexNumberInput.emit(undefined);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
